<template>
  <BoxedFrame
    title="Show"
    class="offer-show"
  >
    <section>
      <Form
        :form-id="showFormId"
        :initial-values="offerSheet || {}"
        @submit.prevent="() => {}"
      >
        <div
          class="section billing"
          :class="{ dirty: isDirty('billing') }"
        >
          <VPInput
            :disabled="loading"
            input-id="billing"
            label="Billing"
            placeholder="Add billing"
          />
        </div>
        <div class="divide-section">
          <div
            class="show-row section"
            :class="{ dirty: isDirty('minimumAge') }"
          >
            <VPInput
              type="integer"
              :disabled="loading"
              input-id="minimumAge"
              label="Min. age"
              placeholder="Add minimum age"
              @blur="saveOfferSheet"
            />
            <VPDateTimeInput
              input-id="doorTime"
              label="Door Time"
              placeholder="Add time"
              :mode-height="135"
              :hide-date="true"
              :clearable="true"
              :text-input="true"
              :timezone="venueTimezone"
              @update="saveOfferSheet"
            />
            <VPDateTimeInput
              input-id="startTime"
              label="Show Time"
              placeholder="Add time"
              mode-height="135"
              :hide-date="true"
              :clearable="true"
              :text-input="true"
              :timezone="venueTimezone"
              @update="saveOfferSheet"
            />
            <VPDateTimeInput
              input-id="endTime"
              label="Curfew Time"
              placeholder="Add time"
              mode-height="135"
              :hide-date="true"
              :clearable="true"
              :text-input="true"
              :timezone="venueTimezone"
              @update="saveOfferSheet"
            />
          </div>

          <div class="divide-section">
            <div
              class="section"
              :class="{ dirty: isDirty('support') }"
            >
              <VPInput
                input-id="support"
                label="Support"
                placeholder="Add support name"
                @blur="saveOfferSheet"
              />
            </div>
            <div
              class="section"
              :class="{ dirty: isDirty('supportGuarantee') }"
            >
              <VPPriceInput
                input-id="supportGuarantee"
                label="Deal - Guarantee"
                placeholder="Add support guarantee ($)"
                class="text-left"
                hide-empty-input
                @blur="saveOfferSheet"
              />
            </div>
            <div
              class="section"
              :class="{ dirty: isDirty('merch') }"
            >
              <VPInput
                input-id="merch"
                label="Merch"
                placeholder="Add merch notes"
                @blur="saveOfferSheet"
              />
            </div>
            <div
              class="section"
              :class="{ dirty: isDirty('exclusivity') }"
            >
              <VPInput
                input-id="exclusivity"
                label="Exclusivity"
                placeholder="Add exclusivity notes"
                @blur="saveOfferSheet"
              />
            </div>
          </div>
        </div>
      </Form>
      <div>
        <div
          class="dropdown-field justify-between section contacts"
          @click="toggleContactsOpen"
        >
          <div class="row-label">
            Contacts
            <span
              v-if="contactMembers"
              class="text-vp-base-900"
            >
              ({{ contactMembers }} {{ inflect(contactMembers, 'member') }})
            </span>
          </div>
          <ChevronUp
            class="arrow"
            :class="{ 'down-arrow': !contactsOpen }"
          />
        </div>
        <div v-show="contactsOpen">
          <div class="section section-row section-header div-3">
            <div>Member name</div>
            <div>Email</div>
            <div>Role</div>
          </div>

          <ContactsRowForm
            v-for="(key, index) in contactsKeys"
            :key="key"
            class="section with-border section-row div-3"
            :initial-values="(contacts && contacts[index]) || {}"
            :form-id="key"
            @blur="onContactsRowBlur(key)"
            @save="onContactsRowSave(key)"
            @clear="onRowDelete(key)"
          />
        </div>
      </div>
    </section>
  </BoxedFrame>
</template>
<script setup lang="ts">
import BoxedFrame from '@/components/BoxedFrame.vue';
import { ref, computed } from 'vue';
import { type SaveOfferSheetParams, useOfferSheetStore } from '@/stores/offer-sheet';
import VPInput from '@/components/form/VPInput.vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPDateTimeInput from '@/components/form/VPDateTimeInput.vue';
import ContactsRowForm from './ContactsRowForm.vue';
import { Form, useFormsStore } from 'vuniform';
import { useOfferSheetFormStore } from '@/stores/offer-sheet-form';
import { ChevronUp } from 'mdue';
import { take } from '@/helpers/takePayload';
import { inflect } from '@/helpers/inflection';
import { UpsertOfferContactInput } from '../../../generated/graphql';

const offerSheetStore = useOfferSheetStore();
const formsStore = useFormsStore();

const showFormId = 'offer-sheets-show';

const contactsOpen = ref<boolean>(false);

const offerSheet = computed(() => offerSheetStore.offerSheet);
const contacts = computed(() => offerSheetStore.offerSheet?.contacts);
const loading = computed(() => offerSheetStore.offerSheetLoading);
const venueTimezone = computed(() => offerSheet.value.venueTimezone);

const offerSheetFormStore = useOfferSheetFormStore();

const contactsKeys = computed(() => offerSheetFormStore.contactsFormIds);

const contactMembers = computed(() => {
  return contactsKeys.value.reduce((acc, key) => {
    const val = formsStore.fieldGetValue(key, 'email' && 'name');
    if (!val) {
      return acc;
    }
    return acc + 1;
  }, 0);
});

const isDirty = (fieldName: string): boolean => {
  return formsStore.isFieldDirty(showFormId, fieldName);
};

const deleteRow = (formId: string) => {
  offerSheetFormStore.deleteContactsRow(formId);
};

const toggleContactsOpen = () => {
  contactsOpen.value = !contactsOpen.value;
};

const saveOfferSheet = async () => {
  if (loading.value || !formsStore.isFormDirty(showFormId)) {
    return;
  }
  const offerSheetParams = formsStore.formGetValues(showFormId);
  const payload: SaveOfferSheetParams = Object.assign({}, offerSheet.value, offerSheetParams);
  payload.offerSheetId = offerSheet.value.id;
  await offerSheetStore.updateOfferSheet(
    take(
      payload,
      'id',
      'support',
      'supportGuarantee',
      'merch',
      'exclusivity',
      'billing',
      'minimumAge',
      'doorTime',
      'startTime',
      'endTime',
      'dealArtistGuarantee',
      'dealGborPercentage',
    ),
    ['dealArtistGuarantee', 'dealGborPercentage', 'supportGuarantee'],
  );

  formsStore.SET_INITIAL_VALUES({
    values: { ...offerSheet.value },
    formId: showFormId,
    clearDirty: true,
  });
};

const onContactsRowBlur = (key: string) => {
  onContactsRowSave(key);
};

const onContactsRowSave = async (formId: string) => {
  if (loading.value || !formsStore.isFormDirty(formId)) {
    return;
  }

  const payload = formsStore.formGetValues<UpsertOfferContactInput>(formId);
  payload.offerSheetId = offerSheet.value.id;
  payload.clientMutationId = formId;

  const contact = await offerSheetStore.upsertOfferContact(payload);

  formsStore.SET_INITIAL_VALUES({
    values: { ...contact },
    formId,
    clearDirty: true,
  });
};

const onRowDelete = async (formId: string) => {
  const id = formsStore.fieldGetValue(formId, 'id');
  if (!id) {
    return;
  }

  await offerSheetStore.deleteOfferContact(id);

  deleteRow(formId);
};
</script>
